import { useState, useMemo, useCallback, ReactNode, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  List,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  PlusCircle,
  CheckCircle2,
  Search,
  Film,
} from 'lucide-react';
import { TwitterIcon, InstagramIcon, LinkedinIcon, FacebookIcon } from './PlatformIcons';
import { api, SocialMediaPost, SocialPostStatus } from '@/lib/api';
import { socialMediaApi } from '../api';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { PostsTable } from './PostsTable';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/query-keys';

// ── Constants ─────────────────────────────────────────────────────────────────

const DAY_HEADERS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

const STATUS_CLASSES: Record<SocialPostStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  scheduled: 'bg-blue-100 text-blue-700',
  publishing: 'bg-yellow-100 text-yellow-700',
  published: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

const STATUS_LABELS: Record<SocialPostStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  publishing: '...',
  published: 'Published',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const PLATFORM_ICONS: Record<string, ReactNode> = {
  twitter: <TwitterIcon className="h-3 w-3" />,
  linkedin: <LinkedinIcon className="h-3 w-3" />,
  facebook: <FacebookIcon className="h-3 w-3" />,
  instagram: <InstagramIcon className="h-3 w-3" />,
};

const MAX_POSTS_PER_CELL = 2;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns YYYY-MM-DD in local time. */
function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// ── Calendar date-range helpers ───────────────────────────────────────────────

/**
 * Returns the visible grid bounds for a month calendar.
 * The grid always starts on the Sunday before (or on) the 1st of the month
 * and spans 42 days (6 rows × 7 cols), so it may include days from the
 * previous and next months.
 */
function computeMonthRange(date: Date): { from: Date; to: Date } {
  const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay()); // back to Sunday
  gridStart.setHours(0, 0, 0, 0);
  const gridEnd = new Date(gridStart);
  gridEnd.setDate(gridEnd.getDate() + 41); // 42 cells total
  gridEnd.setHours(23, 59, 59, 999);
  return { from: gridStart, to: gridEnd };
}

/** Returns the from/to bounds of the 7-day week containing `date`. */
function computeWeekRange(date: Date): { from: Date; to: Date } {
  const from = startOfWeek(date); // midnight Sunday
  const to = addDays(from, 6);
  to.setHours(23, 59, 59, 999);
  return { from, to };
}

// ── Mobile detection ──────────────────────────────────────────────────────────

function useIsMobile(breakpoint = 640): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

// ── MediaThumbnail ────────────────────────────────────────────────────────────

function MediaThumbnail({ fileId }: { fileId: string }) {
  const metaQuery = useQuery({
    queryKey: queryKeys.files.detail(fileId),
    queryFn: () => api.getFile(fileId).then((r) => r.file),
    staleTime: Infinity,
  });

  const urlQuery = useQuery({
    queryKey: queryKeys.files.downloadUrl(fileId),
    queryFn: () => api.getDownloadUrl(fileId).then((r) => r.downloadUrl),
    staleTime: 10 * 60 * 1000,
  });

  const isVideo = metaQuery.data?.mimeType?.startsWith('video/') ?? false;
  const url = urlQuery.data;
  const isLoading = metaQuery.isLoading || urlQuery.isLoading;

  if (isLoading) {
    return <Skeleton className="w-full aspect-square rounded mb-1" />;
  }

  if (!url) return null;

  if (isVideo) {
    return (
      <div className="relative w-full aspect-square rounded mb-1 bg-muted flex items-center justify-center overflow-hidden">
        <Film className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-square rounded mb-1 overflow-hidden bg-muted">
      <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
    </div>
  );
}

// ── CalendarPostCard ──────────────────────────────────────────────────────────

interface CalendarPostCardProps {
  showImage?: boolean;
  post: SocialMediaPost;
  onClick: () => void;
}

function CalendarPostCard({ post, showImage, onClick }: CalendarPostCardProps) {
  const platforms = Object.keys(post.platformContent);
  const isPublished = post.status === 'published';
  const postDateTime = post.publishAt ? new Date(post.publishAt) : new Date(post.createdAt);
  const isPastDate = postDateTime < new Date();
  const time = postDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      className={[
        'text-xs rounded p-1 bg-card border border-border/60 hover:border-border cursor-pointer select-none w-full min-w-0 overflow-hidden',
        isPastDate ? 'opacity-60' : '',
      ].join(' ')}
      onClick={onClick}
    >
      <div
        className={cn(
          'flex items-center gap-1 mb-0.5 min-w-0 overflow-hidden text-gray-300',
          post.mediaFileIds.length > 0 && showImage && 'pb-4',
        )}
      >
        <span className="text-muted-foreground shrink-0 font-medium text-[10px]">{time}</span>
        <span
          className={`inline-flex items-center rounded px-1 py-px text-[10px] font-medium leading-none shrink-0 ${STATUS_CLASSES[post.status]}`}
        >
          {STATUS_LABELS[post.status]}
        </span>
      </div>

      {post.mediaFileIds.length > 0 && showImage && (
        <MediaThumbnail fileId={post.mediaFileIds[0]} />
      )}
      <div
        className={cn(
          'flex items-start gap-1 min-w-0',
          post.mediaFileIds.length > 0 && showImage && 'pt-4',
        )}
      >
        {isPublished && <CheckCircle2 className="h-3 w-3 text-green-500 mt-px shrink-0" />}
        <p
          className={[
            'truncate flex-1 min-w-0 leading-tight',
            isPastDate || isPublished ? 'line-through text-muted-foreground' : '',
          ].join(' ')}
        >
          {/* {post.originalContent || <em>No content</em>} */}
          {post.originalContent || post.title}
        </p>
      </div>
      {platforms.length > 0 && (
        <div className="flex items-center gap-0.5 mt-0.5 text-muted-foreground">
          {platforms.slice(0, 3).map((p) => (
            <span key={p}>{PLATFORM_ICONS[p] ?? <span className="text-[10px]">{p[0]}</span>}</span>
          ))}
          {platforms.length > 3 && <span className="text-[10px]">+{platforms.length - 3}</span>}
        </div>
      )}
    </div>
  );
}

// ── MonthCalendarView ─────────────────────────────────────────────────────────

interface MonthCalendarViewProps {
  currentDate: Date;
  postsByDate: Map<string, SocialMediaPost[]>;
  onNavigateToPost: (postId: string) => void;
  onCreatePost: () => void;
  onSeeAll: (date: Date) => void;
  /** When true, renders compact tappable cells (mobile). Each cell tap → onSeeAll */
  compact?: boolean;
}

function MonthCalendarView({
  currentDate,
  postsByDate,
  onNavigateToPost,
  onCreatePost,
  onSeeAll,
  compact = false,
}: MonthCalendarViewProps) {
  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Always 42 cells (6 rows × 7 cols) for consistent grid height.
  const cells = useMemo(() => {
    const result: { date: Date; isCurrentMonth: boolean }[] = [];
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      result.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      result.push({ date: new Date(year, month, d), isCurrentMonth: true });
    }
    let nextDay = 1;
    while (result.length < 42) {
      result.push({ date: new Date(year, month + 1, nextDay++), isCurrentMonth: false });
    }
    return result;
  }, [year, month]);

  // ── Compact (mobile) month view ─────────────────────────────────────────────
  if (compact) {
    return (
      <div className="select-none w-full">
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAY_HEADERS.map((h) => (
            <div
              key={h}
              className="text-center text-[10px] text-muted-foreground font-medium py-1 !font-mono"
            >
              {h}
            </div>
          ))}
        </div>
        {/* Date grid — compact, tappable, each cell → week view */}
        <div
          className={cn(
            'grid grid-cols-7',
            'relative z-0 w-full',
            "before:content-[''] before:absolute before:left-0 before:right-0 before:top-0 before:bottom-0",
            'before:rounded-2xl before:bg-background',
            'before:shadow-social-list before:pointer-events-none before:-z-10',
          )}
        >
          {cells.map(({ date, isCurrentMonth }, idx) => {
            const key = toDateKey(date);
            const dayPosts = postsByDate.get(key) ?? [];
            const isToday = isSameDay(date, today);
            const isPastDay =
              date < new Date(new Date().setHours(0, 0, 0, 0)) && !isSameDay(date, today);

            return (
              <button
                key={idx}
                onClick={() => onSeeAll(date)}
                className={cn(
                  'flex flex-col items-center py-2 px-0.5 transition-colors',
                  isPastDay ? 'opacity-50' : '',
                  !isCurrentMonth ? 'opacity-30' : '',
                )}
              >
                <span
                  className={cn(
                    'w-7 h-7 flex items-center justify-center text-[12px] rounded-full transition-colors !font-mono',
                    isToday
                      ? 'bg-black text-white font-semibold'
                      : 'text-foreground hover:bg-muted',
                    !isCurrentMonth ? 'text-muted-foreground' : '',
                  )}
                >
                  {date.getDate()}
                </span>
                {/* Status dots */}
                {isCurrentMonth && dayPosts.length > 0 && (
                  <div className="flex items-center gap-px mt-0.5">
                    {dayPosts.slice(0, 3).map((post, i) => {
                      const dotClass: Record<string, string> = {
                        published: 'bg-teal-500',
                        scheduled: 'bg-blue-500',
                        draft: 'bg-gray-400',
                        failed: 'bg-red-500',
                        publishing: 'bg-yellow-500',
                        cancelled: 'bg-gray-300',
                      };
                      return (
                        <span
                          key={i}
                          className={cn(
                            'w-1 h-1 rounded-full shrink-0',
                            dotClass[post.status] ?? 'bg-gray-400',
                          )}
                        />
                      );
                    })}
                    {dayPosts.length > 3 && (
                      <span className="text-[8px] text-muted-foreground leading-none ml-px">
                        +{dayPosts.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Full (desktop) month view ────────────────────────────────────────────────
  return (
    <div className="max-w-full w-full relative flex flex-col gap-1 pb-7">
      <div className="relative max-w-full w-full rounded-[10px]">
        <div className="grid grid-cols-7 !bg-transparent">
          {DAY_HEADERS.map((d) => (
            <div
              key={d}
              className="text-left text-[11px] font-medium  text-[#9A9A9A] uppercase tracking-wide px-2"
            >
              <p className="font-mono px-2 py-3 !bg-transparent">{d}</p>
            </div>
          ))}
        </div>

        {/* 7-row grid — fixed height, no per-cell scroll; overflow shows "See All" */}
        <div
          className={cn(
            'grid grid-cols-7 flex-1 overflow-auto',
            'relative z-0 w-full overflow-visible',
            "before:content-[''] before:absolute before:left-0 before:right-0 before:top-[0px] before:bottom-0",
            'before:rounded-3xl before:bg-background',
            'before:shadow-social-list before:pointer-events-none before:-z-10',
          )}
          style={{ gridTemplateRows: 'repeat(6, minmax(150px, 1fr))' }}
        >
          {cells.map(({ date, isCurrentMonth }, idx) => {
            const key = toDateKey(date);
            const dayPosts = postsByDate.get(key) ?? [];
            const visible = dayPosts.slice(0, MAX_POSTS_PER_CELL);
            const overflow = dayPosts.length - MAX_POSTS_PER_CELL;
            const isToday = isSameDay(date, today);
            const isPastDay =
              date < new Date(new Date().setHours(0, 0, 0, 0)) && !isSameDay(date, today);

            return (
              <div
                key={idx}
                className={[
                  'flex flex-col overflow-hidden min-w-0 gap-0.5 group/cell transition-colors px-2',
                  idx < 7 ? 'pt-2' : '',
                ].join(' ')}
              >
                <div
                  className={cn(
                    idx < 7 ? '' : 'border-t',
                    'flex flex-col overflow-hidden gap-0.5 group/cell py-1 min-h-full',
                  )}
                >
                  <div
                    className={cn(
                      isPastDay ? 'opacity-30' : '',
                      'hover:bg-teal-50/90 p-3',
                      'flex flex-col overflow-hidden gap-0.5 group/cell px-1 min-h-full rounded-md ',
                      idx === 0 ? 'rounded-tl-2xl' : '',
                      idx === 6 ? 'rounded-tr-2xl' : '',
                    )}
                  >
                    {/* Date number — filled pill for today */}
                    <span
                      className={[
                        'text-xs font-medium w-5 h-5 flex items-center justify-center rounded-full shrink-0 !font-mono p-1',
                        isToday
                          ? 'bg-black text-white font-semibold'
                          : !isCurrentMonth
                            ? 'text-muted-foreground/40'
                            : 'text-foreground',
                      ].join(' ')}
                    >
                      {date.getDate()}
                    </span>

                    {/* Posts (clipped, no scroll) */}
                    <div className="flex flex-col gap-0.5 min-h-0 flex-1 overflow-hidden">
                      {visible.map((post) => (
                        <CalendarPostCard
                          key={post.postId}
                          post={post}
                          onClick={() => onNavigateToPost(post.postId)}
                        />
                      ))}
                    </div>

                    {/* Overflow — switches to week view for this date */}
                    {overflow > 0 && (
                      <button
                        className="cursor-pointer  text-[10px] text-teal-600 hover:text-teal-700 font-medium text-left shrink-0"
                        onClick={() => onSeeAll(date)}
                      >
                        See All (+{overflow})
                      </button>
                    )}

                    {/* Empty cell — "+ Create Post" on hover */}
                    {dayPosts.length === 0 && !isPastDay && (
                      <button
                        className="cursor-pointer  hidden group-hover/cell:flex flex-1 items-center justify-center gap-1 text-[11px] text-teal-600 font-medium rounded bg-gradient-to-br from-teal-50/80 to-teal-100/40"
                        onClick={onCreatePost}
                      >
                        <PlusCircle className="h-3 w-3" />
                        Create Post
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── WeekCalendarView ──────────────────────────────────────────────────────────

interface WeekCalendarViewProps {
  currentDate: Date;
  postsByDate: Map<string, SocialMediaPost[]>;
  onNavigateToPost: (postId: string) => void;
  onCreatePost: () => void;
}

function WeekCalendarView({
  currentDate,
  postsByDate,
  onNavigateToPost,
  onCreatePost,
}: WeekCalendarViewProps) {
  const today = new Date();
  const weekStart = startOfWeek(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const isMobile = useIsMobile();

  // Mobile single-day navigation
  const [mobileDayIdx, setMobileDayIdx] = useState(() => {
    const idx = days.findIndex((d) => isSameDay(d, today));
    return idx >= 0 ? idx : 0;
  });

  // Reset to today (or day 0) whenever the week changes
  const weekStartStr = weekStart.toDateString();
  useEffect(() => {
    const ws = startOfWeek(currentDate);
    const d = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
    const idx = d.findIndex((day) => isSameDay(day, today));
    setMobileDayIdx(idx >= 0 ? idx : 0);
  }, [weekStartStr]);

  // Touch swipe for mobile single-day navigation
  const touchStartX = useRef<number | null>(null);
  function handleTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
  }
  function handleTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta < -50) setMobileDayIdx((i) => Math.min(6, i + 1));
    else if (delta > 50) setMobileDayIdx((i) => Math.max(0, i - 1));
    touchStartX.current = null;
  }

  // ── Mobile: single-day view ────────────────────────────────────────────────
  if (isMobile) {
    const mobileDay = days[mobileDayIdx];
    const mobileKey = toDateKey(mobileDay);
    const mobilePosts = postsByDate.get(mobileKey) ?? [];
    const isPastDay =
      mobileDay < new Date(new Date().setHours(0, 0, 0, 0)) && !isSameDay(mobileDay, today);
    const isTodayDay = isSameDay(mobileDay, today);

    return (
      <div className="flex flex-col h-full min-h-0">
        {/* Day selector strip */}
        <div className="grid grid-cols-7 shrink-0 mb-1">
          {days.map((day, i) => {
            const isActive = i === mobileDayIdx;
            const isDayToday = isSameDay(day, today);
            return (
              <button
                key={i}
                onClick={() => setMobileDayIdx(i)}
                className="flex flex-col items-center py-2"
              >
                <span className="text-[10px] text-muted-foreground font-mono">
                  {DAY_HEADERS[i]}
                </span>
                <span
                  className={cn(
                    'w-7 h-7 flex items-center justify-center rounded-full text-[12px] font-mono mt-0.5',
                    isActive
                      ? 'bg-black text-white font-semibold'
                      : isDayToday
                        ? 'bg-black/10 text-foreground font-semibold'
                        : 'text-muted-foreground',
                  )}
                >
                  {day.getDate()}
                </span>
              </button>
            );
          })}
        </div>

        {/* Single-day posts — swipeable */}
        <div
          className={cn(
            'flex-1 overflow-y-auto min-h-[300px] rounded-2xl bg-background shadow-social-list p-3',
            isPastDay ? 'opacity-70' : '',
          )}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {mobilePosts.length > 0 ? (
            <div className="flex flex-col gap-2">
              {mobilePosts.map((post) => (
                <CalendarPostCard
                  key={post.postId}
                  post={post}
                  showImage={true}
                  onClick={() => onNavigateToPost(post.postId)}
                />
              ))}
            </div>
          ) : (
            <button
              className={cn(
                'w-full h-full min-h-[200px] flex flex-col items-center justify-center gap-2 text-sm text-teal-600 font-medium rounded-xl bg-gradient-to-br from-teal-50/80 to-teal-100/40',
                isPastDay && 'pointer-events-none opacity-50',
              )}
              onClick={!isPastDay && !isTodayDay ? onCreatePost : undefined}
            >
              {!isPastDay && (
                <>
                  <PlusCircle className="h-5 w-5" />
                  Create Post
                </>
              )}
              {isPastDay && <span className="text-muted-foreground text-xs">No posts</span>}
            </button>
          )}
        </div>

        {/* Swipe hint */}
        <p className="text-center text-[10px] text-muted-foreground mt-2 font-mono select-none">
          Swipe or tap to navigate days
        </p>
      </div>
    );
  }

  // ── Desktop: 7-column view ────────────────────────────────────────────────
  return (
    <div className="max-w-full w-full relative flex flex-col gap-1 pb-7 h-full min-h-0">
      <div className="relative max-w-full w-full rounded-[10px] bg-transparent">
        {/* Day column headers */}
        <div className="grid grid-cols-7 shrink-0 !bg-transparent">
          {days.map((day, i) => {
            const isToday = isSameDay(day, today);
            return (
              <div key={i} className="flex gap-1 items-center py-2 px-1 text-left">
                <p className="text-[11px] font-normal text-muted-foreground tracking-wider font-mono text-[#9A9A9A]">
                  {DAY_HEADERS[i]}
                </p>
                <div
                  className={[
                    'text-[11px] font-normal mt-0.5 h-7 w-7 flex items-center justify-center rounded-full !font-mono text-[#9A9A9A]',
                    isToday ? 'bg-[#9A9A9A] text-white' : '',
                  ].join(' ')}
                >
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Scrollable post columns — all 7 scroll together */}
        <div
          className={cn(
            'flex flex-1 overflow-y-auto min-h-[400px]',
            'relative z-0 w-full overflow-visible',
            "before:content-[''] before:absolute before:left-0 before:right-0 before:top-[0px] before:bottom-0",
            'before:rounded-3xl before:bg-background',
            'before:shadow-social-list before:pointer-events-none before:-z-10',
          )}
        >
          {days.map((day, i) => {
            const key = toDateKey(day);
            const dayPosts = postsByDate.get(key) ?? [];
            const isPastDay =
              day < new Date(new Date().setHours(0, 0, 0, 0)) && !isSameDay(day, today);

            return (
              <div
                key={i}
                className={[
                  'flex-1 min-w-0 overflow-hidden flex flex-col gap-1 p-1.5 border-l first:border-l-0 min-h-[260px] group/cell transition-colors py-5',
                  isPastDay ? 'opacity-50' : '',
                  'hover:bg-teal-50/30',
                ].join(' ')}
              >
                {dayPosts.length > 0 ? (
                  dayPosts.map((post) => (
                    <CalendarPostCard
                      key={post.postId}
                      post={post}
                      showImage={true}
                      onClick={() => onNavigateToPost(post.postId)}
                    />
                  ))
                ) : (
                  <button
                    className={cn(
                      isPastDay && '!hidden',
                      'cursor-pointer hidden group-hover/cell:flex flex-1 items-center justify-center gap-1 text-[11px] text-teal-600 font-medium rounded bg-gradient-to-br from-teal-50/80 to-teal-100/40',
                    )}
                    onClick={onCreatePost}
                  >
                    <PlusCircle className="h-3 w-3" />
                    Create
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── ListView ──────────────────────────────────────────────────────────────────

interface ListViewProps {
  posts: SocialMediaPost[];
  error: string | null;
  total: number;
  isLoading: boolean;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  setActiveTab: React.Dispatch<React.SetStateAction<StatusTab>>;
  setDebouncedSearch: React.Dispatch<React.SetStateAction<string>>;
  debouncedSearch: string;
  lastItem: number;
  statusFilter: 'all' | SocialPostStatus;
  deletePost: (postId: string) => Promise<void>;
}

const TABS: { label: string; value: StatusTab }[] = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Published', value: 'published' },
  { label: 'Failed', value: 'failed' },
];

type StatusTab = 'all' | SocialPostStatus;

function ListView({
  posts,
  total,
  isLoading,
  error,
  hasPrevPage,
  hasNextPage,
  goToNextPage,
  goToPrevPage,
  setActiveTab,
  setDebouncedSearch,
  debouncedSearch,
  lastItem,
  statusFilter,
  deletePost,
}: ListViewProps) {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleTabChange(tab: StatusTab) {
    setActiveTab(tab);
  }

  function handleSearchChange(value: string) {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  }

  return (
    <div className="relative lg:space-y-4">
      {/* Header */}
      {/* Filters row — mobile: search on top, filters below; desktop: side by side */}
      <div className="p-3 pb-5 relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        {/* Search — full width on mobile, fixed width on desktop */}
        <div className="relative w-full sm:w-64 sm:order-2">
          <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search posts..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className={cn(
              '!pl-8 w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
              'h-[40px] px-3 py-3 !font-mono',
              'text-primary',
              'placeholder-[var(--input-placeholder)]',
            )}
          />
        </div>

        {/* Status filter tabs — scrollable on mobile */}
        <div className="lg:w-fit w-full  flex items-center gap-1 rounded-xl p-1 cursor-pointer shadow-social-softer overflow-x-auto no-scrollbar sm:order-1 bg-background">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              className={`flex-1 cursor-pointer px-3 py-1.5 transition-colors rounded-xl !font-mono lg:text-sm text-[10px] transition-colors whitespace-nowrap ${
                statusFilter === tab.value
                  ? 'text-foreground bg-muted'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {tab.label.toLocaleUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Posts list */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">
            {debouncedSearch ? 'No posts match your search.' : 'No posts yet.'}
          </p>
          {!debouncedSearch && (
            <Button onClick={() => navigate('/a/arche_social_media/compose')}>Create a post</Button>
          )}
        </div>
      ) : (
        <PostsTable posts={posts} deletePost={deletePost} />
      )}

      {/* Pagination footer */}
      {total > 0 && (
        <div className="flex justify-end items-center gap-2 px-4 py-1 mt-2">
          <span className="text-sm text-[#1A1A1A]">{lastItem} //</span>
          <span className="text-sm text-[#9A9A9A]">
            {total} ITEM{Number(total) >= 0 ? 'S' : ''}
          </span>

          <button
            onClick={goToPrevPage}
            disabled={!hasPrevPage}
            className="text-[var(--text-primary)] ml-4 px-3 py-2 text-sm  bg-transparent disabled:opacity-40 disabled:bg-transparent disabled:text-[var(--color-text-disabled)] disabled:cursor-not-allowed cursor-pointer hover:bg-[var(--color-bg-inverse)] hover:text-[var(--color-text-inverse)] border border-[var(--border-disabled)] rounded-full"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={goToNextPage}
            disabled={!hasNextPage}
            className="text-[var(--text-primary)] px-3 py-2 text-sm bg-transparent disabled:opacity-40 disabled:bg-transparent disabled:text-[var(--color-text-disabled)] disabled:cursor-not-allowed cursor-pointer hover:bg-[var(--color-bg-inverse)] hover:text-[var(--color-text-inverse)] border border-[var(--border-disabled)] rounded-full "
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── PublishingCalendar ────────────────────────────────────────────────────────

export interface PublishingCalendarProps {
  posts: SocialMediaPost[];
  onCreatePost: () => void;
  deletePost: (postId: string) => Promise<void>;
  error: string | null;
  total: number;
  isLoading: boolean;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  setActiveTab: React.Dispatch<React.SetStateAction<StatusTab>>;
  setDebouncedSearch: React.Dispatch<React.SetStateAction<string>>;
  debouncedSearch: string;
  lastItem: number;
  statusFilter: 'all' | SocialPostStatus;
}

export function PublishingCalendar({
  posts,
  error,
  onCreatePost,
  total,
  isLoading,
  hasPrevPage,
  hasNextPage,
  goToNextPage,
  goToPrevPage,
  setActiveTab,
  setDebouncedSearch,
  debouncedSearch,
  lastItem,
  statusFilter,
  deletePost,
}: PublishingCalendarProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { selectedHolderId } = useHolderSwitcher();

  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  const [calendarRange, setCalendarRange] = useState<'week' | 'month'>('week');
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());

  // Compute the visible date range for the current calendar state.
  // Changing month/week automatically yields a new range → new query key → re-fetch.
  const visibleRange = useMemo((): { from: Date; to: Date } | null => {
    if (viewMode !== 'calendar') return null;
    return calendarRange === 'month'
      ? computeMonthRange(currentDate)
      : computeWeekRange(currentDate);
  }, [viewMode, calendarRange, currentDate]);

  // Fetch posts for the visible calendar range internally.
  // Posts are sorted by COALESCE(publish_at, created_at) ASC on the backend so
  // a draft scheduled months later appears on its scheduled date, not created date.
  const calendarPostsQuery = useQuery({
    queryKey:
      visibleRange && selectedHolderId
        ? queryKeys.socialMedia.calendarPosts(
            selectedHolderId,
            visibleRange.from.toISOString(),
            visibleRange.to.toISOString(),
          )
        : queryKeys.socialMedia.calendarPosts('disabled', 'disabled', 'disabled'),
    queryFn:
      visibleRange && selectedHolderId
        ? () =>
            socialMediaApi
              .listSocialMediaPosts(selectedHolderId, {
                from: visibleRange.from.toISOString(),
                to: visibleRange.to.toISOString(),
                limit: 500,
              })
              .then((r) => r.posts)
        : () => Promise.resolve<SocialMediaPost[]>([]),
    enabled: !!visibleRange && !!selectedHolderId,
    staleTime: 2 * 60 * 1000, // 2 min — short enough to pick up newly scheduled posts
  });

  const calendarPosts = calendarPostsQuery.data ?? [];

  // Build date → posts map from the range-fetched posts.
  // Key is derived from publishAt when set, otherwise createdAt — matching
  // the COALESCE ordering used by the backend.
  const postsByDate = useMemo(() => {
    const map = new Map<string, SocialMediaPost[]>();
    for (const post of calendarPosts) {
      const key = post.publishAt
        ? toDateKey(new Date(post.publishAt))
        : toDateKey(new Date(post.createdAt));
      const existing = map.get(key) ?? [];
      map.set(key, [...existing, post]);
    }
    // Posts arrive pre-sorted from the backend; keep that order per day.
    return map;
  }, [calendarPosts]);

  const handleNavigateToPost = useCallback(
    (postId: string) => {
      navigate(`/a/arche_social_media/compose/${postId}`);
    },
    [navigate],
  );

  // "See All" on a month cell → jump to week view containing that date
  const handleSeeAll = useCallback((date: Date) => {
    setCurrentDate(date);
    setCalendarRange('week');
  }, []);

  return (
    <div className="flex flex-col w-full gap-3">
      {/* ── Toolbar ── */}
      {/* Mobile: stacked 2-row layout */}
      <div className="sm:hidden flex flex-col gap-2 px-3">
        {/* Row 1: view toggle + range selector */}
        <div className="flex items-center justify-between">
          <div className="flex overflow-hidden shrink-0 gap-1">
            <button
              className={[
                'p-2 transition-colors rounded-md cursor-pointer',
                viewMode === 'list'
                  ? 'text-foreground bg-muted'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              ].join(' ')}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              className={[
                'p-2 transition-colors rounded-md cursor-pointer',
                viewMode === 'calendar'
                  ? 'text-foreground bg-muted'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              ].join(' ')}
              onClick={() => setViewMode('calendar')}
              title="Calendar view"
            >
              <CalendarDays className="h-4 w-4" />
            </button>
          </div>
          {viewMode === 'calendar' && (
            <div className="cursor-pointer shadow-social-medium flex rounded-md overflow-hidden text-sm shrink-0 bg-background p-1">
              <button
                className={[
                  'text-[10px] !font-mono px-3 py-1.5 transition-colors rounded-md cursor-pointer',
                  calendarRange === 'week'
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground',
                ].join(' ')}
                onClick={() => setCalendarRange('week')}
              >
                Week
              </button>
              <button
                className={[
                  'text-[10px] !font-mono px-3 py-1.5 transition-colors rounded-md cursor-pointer',
                  calendarRange === 'month'
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground',
                ].join(' ')}
                onClick={() => setCalendarRange('month')}
              >
                Month
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop: existing single-row layout */}
      <div className="hidden sm:flex items-center gap-2 flex-wrap justify-between">
        {/* List / Calendar view toggle */}
        <div className="flex overflow-hidden shrink-0 gap-1 cursor-pointer">
          <button
            className={[
              'p-2 transition-colors rounded-md cursor-pointer',
              viewMode === 'list'
                ? 'text-foreground text-foreground bg-muted'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted',
            ].join(' ')}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            className={[
              'p-2 transition-colors rounded-md cursor-pointer',
              viewMode === 'calendar'
                ? 'text-foreground text-foreground bg-muted'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted',
            ].join(' ')}
            onClick={() => setViewMode('calendar')}
            title="Calendar view"
          >
            <CalendarDays className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-1">
          {/* Week / Month segmented control (calendar only) */}
          {viewMode === 'calendar' && (
            <div className="cursor-pointer shadow-social-medium flex rounded-md overflow-hidden text-sm shrink-0 bg-background p-1">
              <button
                className={[
                  '!font-mono px-3 py-1.5 transition-colors rounded-md cursor-pointer',
                  calendarRange === 'week'
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground',
                ].join(' ')}
                onClick={() => setCalendarRange('week')}
              >
                Week
              </button>
              <button
                className={[
                  '!font-mono px-3 py-1.5 transition-colors rounded-md cursor-pointer',
                  calendarRange === 'month'
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground',
                ].join(' ')}
                onClick={() => setCalendarRange('month')}
              >
                Month
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      {viewMode === 'list' ? (
        <ListView
          posts={posts}
          deletePost={deletePost}
          error={error}
          total={total}
          isLoading={isLoading}
          hasPrevPage={hasPrevPage}
          hasNextPage={hasNextPage}
          goToNextPage={goToNextPage}
          goToPrevPage={goToPrevPage}
          setActiveTab={setActiveTab}
          setDebouncedSearch={setDebouncedSearch}
          debouncedSearch={debouncedSearch}
          lastItem={lastItem}
          statusFilter={statusFilter}
        />
      ) : (
        // Fixed-height calendar container. Month cells clip overflow (no scroll);
        // week view scrolls its content area. Both share the same max-height.
        <div className="space-y-4">
          {calendarRange === 'month' ? (
            <MonthCalendarView
              currentDate={currentDate}
              postsByDate={postsByDate}
              onNavigateToPost={handleNavigateToPost}
              onCreatePost={onCreatePost}
              onSeeAll={handleSeeAll}
              compact={isMobile}
            />
          ) : (
            <WeekCalendarView
              currentDate={currentDate}
              postsByDate={postsByDate}
              onNavigateToPost={handleNavigateToPost}
              onCreatePost={onCreatePost}
            />
          )}
        </div>
      )}
    </div>
  );
}
